import { useState, useEffect } from 'react';
import { CheckCircleIcon, CrossCrossIcon } from '@clariness/clarikit-icons';
import {
  Box,
  Button,
  DateTimePicker,
  Icon,
  Select,
  SelectOptionInterface,
  Text,
} from '@clariness/clarikit-react';
import { FormatStatus } from 'utils/format';
import { ReferralDto, Protocol801ParticipationUpdateDto } from 'common/api';
import { useIntl } from 'react-intl';

interface Props {
  referral?: ReferralDto;
  Protocol801Participation: (value: any) => void;
}

export const Protocol801 = ({ referral, Protocol801Participation }: Props) => {
  const intl = useIntl();

  const [isProtocol801Edit, setIsProtocol801Edit] = useState<boolean>(false);
  const [date, setDate] = useState<Date>();
  const [outcome, setOutcome] = useState<
    Protocol801ParticipationUpdateDto.outcome | undefined
  >();

  const outcomeOptions: SelectOptionInterface[] = Object.keys(
    Protocol801ParticipationUpdateDto.outcome
  ).map((key) => ({
    id: key,
    value: key,
    label: FormatStatus(key as Protocol801ParticipationUpdateDto.outcome),
  }));

  useEffect(() => {
    if (
      referral?.protocol801Participation.date &&
      referral.protocol801Participation.outcome
    ) {
      setDate(new Date(referral.protocol801Participation.date));
      setOutcome(referral.protocol801Participation?.outcome);
    }
  }, [referral]);

  const formattedDate = date?.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const handelSubmit = () => {
    if (outcome && date && referral?.id) {
      const payload: Protocol801ParticipationUpdateDto = {
        outcome,
        date: date.toISOString(),
        referralId: referral.id,
      };
      Protocol801Participation(payload);
      setIsProtocol801Edit(false);
    }
  };

  return (
    <>
      {Boolean(
        (!referral?.protocol801Participation.date &&
          !referral?.protocol801Participation.outcome) ||
          isProtocol801Edit
      ) && (
        <Box
          display="flex"
          flexDirection="column"
          gap="16"
          width="100%"
          style={{ maxWidth: '360px' }}
        >
          <Select
            label={intl.formatMessage({
              id: 'is.patient_details_modal.basic_details.protocol_801.outcome',
              defaultMessage: 'Outcome',
            })}
            placeholder={intl.formatMessage({
              id: 'is.main_page.referrals_table_label.select',
              defaultMessage: 'Select',
            })}
            dropDownOptionList={outcomeOptions}
            defaultSelectedValue={outcomeOptions.find(
              (option) => option.value === String(outcome)
            )}
            onChange={(option) =>
              setOutcome(
                option?.value as
                  | Protocol801ParticipationUpdateDto.outcome
                  | undefined
              )
            }
          />
          <DateTimePicker
            value={formattedDate}
            label={intl.formatMessage({
              id: 'is.update_patient_status.date_picker_label',
              defaultMessage: 'Date',
            })}
            placeholderText={intl.formatMessage({
              id: 'is.update_patient_status.date_picker_placeholder',
              defaultMessage: 'Select date',
            })}
            onChange={(value) => {
              setDate(value instanceof Date ? value : undefined);
            }}
          />
          <Box display="flex" justifyContent="flex-end" gap="8">
            <Button variant="tertiary" size="m" onClick={handelSubmit}>
              {intl.formatMessage({ id: 'is.add', defaultMessage: 'Add' })}
            </Button>

            {Boolean(
              referral?.protocol801Participation?.date &&
                referral?.protocol801Participation?.outcome
            ) && (
              <Button
                variant="tertiary"
                size="m"
                color="color-gray-variant-2"
                onClick={() => setIsProtocol801Edit(false)}
              >
                {intl.formatMessage({
                  id: 'is.update_protocol_modal.cancel_button',
                  defaultMessage: 'Cancel',
                })}
              </Button>
            )}
          </Box>
        </Box>
      )}
      {referral?.protocol801Participation?.date &&
        referral?.protocol801Participation?.outcome &&
        !isProtocol801Edit && (
          <Box display="flex" gap="16">
            <Box>
              {referral.protocol801Participation.outcome ===
              Protocol801ParticipationUpdateDto.outcome.CONSENTED_RANDOMIZED ? (
                <Icon source={CheckCircleIcon} color="color-green-quaternary" />
              ) : (
                <Icon source={CrossCrossIcon} color="color-gray-variant-2" />
              )}
            </Box>
            <Box display="flex" flexDirection="column" gap="16">
              <Text as="p" variant="bodyMd">
                {FormatStatus(referral.protocol801Participation.outcome)}
              </Text>
              <Text as="p" variant="bodyMd">
                {formattedDate}
              </Text>
            </Box>

            <Button
              variant="tertiary"
              size="m"
              onClick={() => setIsProtocol801Edit(true)}
            >
              {intl.formatMessage({ id: 'is.edit', defaultMessage: 'Edit' })}
            </Button>
          </Box>
        )}
    </>
  );
};
